import React from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../common/colors";
import type {
  OutreachEvent,
  OutreachSource
} from "src/providers/compliance/complianceTypes";
import { localDate } from "src/providers/compliance/complianceClock";
import { outreachStyle } from "./complianceColors";
import { ChartSwatch, DailyBarChart, integerTicks } from "./DailyBarChart";
import { formatDay } from "./dayTimeline";

interface OutreachTimelineProps {
  events: OutreachEvent[];
  dates: string[];
  lastContactDays: number | null;
  timezone: string;
}

// Stacking order, bottom first. Other outreach types would be added as more segments.
const SOURCES = [
  { source: "intercom" as const },
  { source: "zendesk" as const }
];

const SOURCE_TOOLTIPS: Record<OutreachSource, string> = {
  intercom: "Automated outreach (Intercom)",
  zendesk: "Manual outreach (Zendesk)"
};

const CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  chat: "Chat",
  phone: "Call",
  sms: "Text"
};

function EventTooltip({
  events,
  timezone
}: {
  events: OutreachEvent[];
  timezone: string;
}): JSX.Element {
  if (events.length === 0) return <></>;

  return (
    <Box sx={{ fontFamily: "Work Sans, sans-serif", fontSize: 12 }}>
      {events.map((event, idx) => {
        const date = new Date(event.occurredAt);
        const formatted = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          timeZone: timezone
        });
        const time = date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: timezone
        });
        const channelLabel = CHANNEL_LABELS[event.channel] || event.channel;

        return (
          <Box
            key={event.externalId}
            sx={{ mb: idx < events.length - 1 ? 1 : 0 }}
          >
            <Box sx={{ fontWeight: 600 }}>
              {SOURCE_TOOLTIPS[event.source]} · {formatted}, {time}
            </Box>
            <Box>{channelLabel}</Box>
          </Box>
        );
      })}
    </Box>
  );
}

export function OutreachTimeline({
  events,
  dates,
  lastContactDays,
  timezone
}: OutreachTimelineProps): JSX.Element {
  const byDate = new Map<string, OutreachEvent[]>();
  for (const event of events) {
    const day = localDate(new Date(event.occurredAt), timezone);
    byDate.set(day, [...(byDate.get(day) ?? []), event]);
  }

  const dateSet = new Set(dates);
  const eventsInRange = events.filter((e) => {
    const day = localDate(new Date(e.occurredAt), timezone);
    return dateSet.has(day);
  });

  const shown = SOURCES.filter((s) =>
    eventsInRange.some((e) => e.source === s.source)
  );
  const todayDate = dates[dates.length - 1];

  const bars = dates.map((date) => {
    const dayEvents = byDate.get(date) ?? [];
    return {
      date,
      segments: shown.map(({ source }) => ({
        value: dayEvents.filter((e) => e.source === source).length,
        ...outreachStyle(source)
      })),
      tooltip:
        dayEvents.length > 0 ? (
          <EventTooltip events={dayEvents} timezone={timezone} />
        ) : (
          `${formatDay(date)}: no outreach`
        )
    };
  });
  const yMax = Math.max(
    1,
    ...bars.map((b) => b.segments.reduce((sum, seg) => sum + seg.value, 0))
  );

  return (
    <Box>
      <DailyBarChart
        dates={dates}
        bars={bars}
        yMax={yMax}
        yTicks={integerTicks(yMax)}
        formatTick={(t) => String(t)}
        height={48}
        todayDate={todayDate}
        yLabel="# attempts"
      />
      {shown.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
          {shown.map(({ source }) => {
            return (
              <Box
                key={source}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <ChartSwatch {...outreachStyle(source)} />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: "Work Sans, sans-serif",
                    color: COLORS.GREY_900
                  }}
                >
                  {SOURCE_TOOLTIPS[source]}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
      {eventsInRange.length === 0 && (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "Work Sans, sans-serif",
            color: COLORS.GREY_900,
            mt: 0.5
          }}
        >
          {lastContactDays === null
            ? "No contact recorded"
            : `Last contact: ${lastContactDays}d`}
        </Typography>
      )}
    </Box>
  );
}
